import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMeWithOrganizationImpersonation } from "../../hooks/organizationImpersonation";

import "./styles.scss";

const AdminViewBannerVisibleContext = React.createContext<boolean>(false);
const SetForceShowAdminViewBannerContext = React.createContext<
  (visible: boolean) => void
>(() => {});
const HideAdminViewBannerContext = React.createContext<() => void>(() => {});

// Lets a page request the banner even when the admin isn't impersonating
// an organization (e.g. FacilityDetailsPage visited directly, with no
// organization in the URL).
export const useSetForceShowAdminViewBanner = () =>
  useContext(SetForceShowAdminViewBannerContext);

const useHideAdminViewBanner = () => useContext(HideAdminViewBannerContext);

interface IAdminViewBannerProviderProps {
  children?: React.ReactNode;
}

export const AdminViewBannerProvider: React.FunctionComponent<
  IAdminViewBannerProviderProps
> = ({ children }) => {
  const me = useMeWithOrganizationImpersonation();
  const [forceShow, setForceShow] = useState(false);
  const [hidden, setHidden] = useState(false);

  const shouldShow = !!me?.is_impersonating || forceShow;

  return (
    <AdminViewBannerVisibleContext.Provider value={shouldShow && !hidden}>
      <SetForceShowAdminViewBannerContext.Provider value={setForceShow}>
        <HideAdminViewBannerContext.Provider value={() => setHidden(true)}>
          {children}
        </HideAdminViewBannerContext.Provider>
      </SetForceShowAdminViewBannerContext.Provider>
    </AdminViewBannerVisibleContext.Provider>
  );
};

// The single, global instance of the banner. Mounted once so it always
// renders full-width, fixed to the bottom of the page over the content,
// regardless of which page triggered it.
export const GlobalAdminViewBanner: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const visible = useContext(AdminViewBannerVisibleContext);
  const hideBanner = useHideAdminViewBanner();

  if (!visible) {
    return null;
  }

  return (
    <div className="admin-view-banner">
      <p>{t("adminViewBanner.explanation")}</p>
      <button type="button" className="hide-button" onClick={hideBanner}>
        {t("adminViewBanner.hide")}
      </button>
    </div>
  );
};
